import { z } from "zod";

export const ABSOLUTE_TEAM_MEMBER_GROUP_TYPE_VALUES = [
  "department",
  "location",
  "class",
  "team_member",
  "pay_type",
  "employment_type",
  "title",
  "crew",
  "all_team_members",
  "job_supervisor",
  "job_superintendent",
  "job_project_manager",
  "direct_managers",
  "crew_lead",
  "department_head",
  "pay_rate_group",
  "pay_schedule",
  "company_entity",
  "admin",
] as const;

export const TEAM_MEMBER_GROUP_TYPE_VALUES = [
  ...ABSOLUTE_TEAM_MEMBER_GROUP_TYPE_VALUES,
  "direct_reports",
  "direct_manager",
  "self",
  "credit_card_approver",
] as const;

export type TeamMemberGroupType = (typeof TEAM_MEMBER_GROUP_TYPE_VALUES)[number];

export const TEAM_MEMBER_GROUP_PAY_TYPE_VALUES = [
  "hourly_all",
  "hourly_union",
  "hourly_non_union",
  "salary",
] as const;

export type TeamMemberGroup = { type: TeamMemberGroupType; value?: string | null };

/**
 * Zod schema for `TeamMemberGroup`, structurally equivalent to the type alias
 * above; kept separate so the alias stays canonical until a `tsd`/`expect-type`
 * pass confirms equivalence.
 */
export const TeamMemberGroupZodSchema = z.object({
  type: z.enum(TEAM_MEMBER_GROUP_TYPE_VALUES),
  value: z.string().nullable().optional(),
});

/**
 * For fields that gate access by group (`eligible_team_member_groups` and friends). Two constraints
 * the loose schema above can't express, both of which fail silently:
 *
 * - Absolute types only. `isTeamMemberEligibleForAbsoluteGroups` discards the relative types and
 *   then treats the record as unrestricted, so a relative-only rule set grants everyone access.
 * - `value` is always set. Value-bearing types take the entity id (pay_type takes one of
 *   hourly_all / hourly_union / hourly_non_union / salary); types resolved by type alone repeat the
 *   type name, matching what the dashboard's group picker writes. A value-less group keys to
 *   nothing in that picker, so it renders as unselected and the next save drops it.
 */
export const AbsoluteTeamMemberGroupZodSchema = z.object({
  type: z.enum(ABSOLUTE_TEAM_MEMBER_GROUP_TYPE_VALUES),
  value: z.string().min(1),
});
